/**
 * useGovernanceLens - Custom Hook for Governance Document Lens Analysis
 * ============================================================================
 * DC-1040-1046: Hook for fetching and managing governance document lens data
 *
 * Features:
 * - Fetch available lenses and regulations
 * - Run Tier 1 multi-lens analysis
 * - Run Tier 2 deep analysis
 * - Loading and error states
 *
 * Pattern: Follows useFDACredibility.ts (DC-930)
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  GovernanceLensId,
  RegulationSchemaId,
  LensInfo,
  LensResult,
  RegulationSchemaInfo,
  GovernanceLensResult,
  DeepAnalysisResult,
  LensesResponse,
  RegulationsResponse,
  AnalyzeResponse,
  DeepAnalyzeResponse,
  Tier1ResultsResponse,
  Tier1Finding,
} from './types';

// ============================================================================
// Types
// ============================================================================

export interface UseGovernanceLensOptions {
  /** Auto-fetch metadata on mount */
  autoFetch?: boolean;
  /** Document ID to work with */
  documentId?: string;
}

export interface UseGovernanceLensReturn {
  // Lens metadata
  availableLenses: LensInfo[];
  availableRegulations: RegulationSchemaInfo[];

  // Tier 1 state
  tier1Result: GovernanceLensResult | null;
  tier1Loading: boolean;
  tier1Running: boolean;

  // Tier 2 state
  deepAnalysisResult: DeepAnalysisResult | null;
  deepAnalysisLoading: boolean;
  deepAnalysisRunning: boolean;

  // Error state
  error: Error | null;

  // Actions
  fetchLenses: () => Promise<void>;
  fetchRegulations: () => Promise<void>;
  fetchTier1Results: (documentId: string) => Promise<void>;
  runTier1Analysis: (documentId: string, lenses?: GovernanceLensId[]) => Promise<GovernanceLensResult | null>;
  fetchDeepAnalysis: (documentId: string, regulationId: RegulationSchemaId) => Promise<void>;
  runDeepAnalysis: (documentId: string, regulationId: RegulationSchemaId) => Promise<DeepAnalysisResult | null>;
  clearError: () => void;
  reset: () => void;
}

// ============================================================================
// API Base URL
// ============================================================================

const API_BASE = '/api/v1/governance-documents';

// ============================================================================
// Hook Implementation
// ============================================================================

export function useGovernanceLens(
  options: UseGovernanceLensOptions = {}
): UseGovernanceLensReturn {
  const { autoFetch = true, documentId } = options;

  // Metadata state
  const [availableLenses, setAvailableLenses] = useState<LensInfo[]>([]);
  const [availableRegulations, setAvailableRegulations] = useState<RegulationSchemaInfo[]>([]);

  // Tier 1 state
  const [tier1Result, setTier1Result] = useState<GovernanceLensResult | null>(null);
  const [tier1Loading, setTier1Loading] = useState(false);
  const [tier1Running, setTier1Running] = useState(false);

  // Tier 2 state
  const [deepAnalysisResult, setDeepAnalysisResult] = useState<DeepAnalysisResult | null>(null);
  const [deepAnalysisLoading, setDeepAnalysisLoading] = useState(false);
  const [deepAnalysisRunning, setDeepAnalysisRunning] = useState(false);

  // Error state
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch available lenses
   */
  const fetchLenses = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/lenses`);

      if (!response.ok) {
        throw new Error(`Failed to fetch lenses: ${response.statusText}`);
      }

      const data: LensesResponse = await response.json();

      if (data.success && data.result) {
        setAvailableLenses(data.result.lenses);
      } else {
        throw new Error(data.error || 'Failed to fetch lenses');
      }
    } catch (err) {
      console.error('[useGovernanceLens] Fetch lenses error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, []);

  /**
   * Fetch available regulations
   */
  const fetchRegulations = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/regulations`);

      if (!response.ok) {
        throw new Error(`Failed to fetch regulations: ${response.statusText}`);
      }

      const data: RegulationsResponse = await response.json();

      if (data.success && data.result) {
        setAvailableRegulations(data.result.schemas);
      } else {
        throw new Error(data.error || 'Failed to fetch regulations');
      }
    } catch (err) {
      console.error('[useGovernanceLens] Fetch regulations error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, []);

  /**
   * Fetch existing Tier 1 results for a document
   */
  const fetchTier1Results = useCallback(async (docId: string) => {
    if (!docId) {
      setError(new Error('Document ID is required'));
      return;
    }

    try {
      setTier1Loading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/${docId}/tier1-results`);

      if (response.status === 404) {
        // No analysis yet - this is not an error
        setTier1Result(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch Tier 1 results: ${response.statusText}`);
      }

      const data: Tier1ResultsResponse = await response.json();

      if (data.success && data.result) {
        // Convert to GovernanceLensResult format
        const lensResults: Partial<Record<GovernanceLensId, LensResult>> = {};
        for (const [lensId, score] of Object.entries(data.result.lens_scores)) {
          lensResults[lensId as GovernanceLensId] = {
            lens_id: lensId as GovernanceLensId,
            score,
            concepts_covered: [],
            concepts_missing: [],
            total_concepts: 0,
            coverage_percentage: score,
            findings: data.result.findings.filter(f => f.lens_id === lensId),
            analyzed_at: data.result.analyzed_at,
          };
        }

        setTier1Result({
          document_id: data.result.document_id,
          org_id: '',
          overall_score: Object.values(data.result.lens_scores).reduce((a, b) => a + b, 0) /
            Object.values(data.result.lens_scores).length || 0,
          lens_results: lensResults,
          all_findings: data.result.findings,
          findings_summary: summarizeFindings(data.result.findings),
          weights_used: {} as Record<GovernanceLensId, number>,
          detected_regulations: data.result.detected_regulations,
          analyzed_at: data.result.analyzed_at,
          analysis_version: '1.0.0',
        });
      } else {
        setTier1Result(null);
      }
    } catch (err) {
      console.error('[useGovernanceLens] Fetch Tier 1 error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setTier1Loading(false);
    }
  }, []);

  /**
   * Run Tier 1 multi-lens analysis
   */
  const runTier1Analysis = useCallback(
    async (docId: string, selectedLenses?: GovernanceLensId[]): Promise<GovernanceLensResult | null> => {
      if (!docId) {
        setError(new Error('Document ID is required'));
        return null;
      }

      try {
        setTier1Running(true);
        setError(null);

        const response = await fetch(`${API_BASE}/${docId}/analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selectedLenses,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to run Tier 1 analysis: ${response.statusText}`);
        }

        const data: AnalyzeResponse = await response.json();

        if (data.success && data.result) {
          setTier1Result(data.result);
          return data.result;
        }

        return null;
      } catch (err) {
        console.error('[useGovernanceLens] Tier 1 analysis error:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      } finally {
        setTier1Running(false);
      }
    },
    []
  );

  /**
   * Fetch existing deep analysis for a document and regulation
   */
  const fetchDeepAnalysis = useCallback(
    async (docId: string, regulationId: RegulationSchemaId) => {
      if (!docId || !regulationId) {
        setError(new Error('Document ID and Regulation ID are required'));
        return;
      }

      try {
        setDeepAnalysisLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/${docId}/deep-analysis/${regulationId}`);

        if (response.status === 404) {
          // No analysis yet - this is not an error
          setDeepAnalysisResult(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch deep analysis: ${response.statusText}`);
        }

        const data: DeepAnalyzeResponse = await response.json();

        if (data.success && data.result) {
          setDeepAnalysisResult(data.result);
        } else {
          setDeepAnalysisResult(null);
        }
      } catch (err) {
        console.error('[useGovernanceLens] Fetch deep analysis error:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setDeepAnalysisLoading(false);
      }
    },
    []
  );

  /**
   * Run Tier 2 deep analysis
   */
  const runDeepAnalysis = useCallback(
    async (docId: string, regulationId: RegulationSchemaId): Promise<DeepAnalysisResult | null> => {
      if (!docId) {
        setError(new Error('Document ID is required'));
        return null;
      }

      if (!regulationId) {
        setError(new Error('Regulation ID is required'));
        return null;
      }

      try {
        setDeepAnalysisRunning(true);
        setError(null);

        const response = await fetch(`${API_BASE}/${docId}/deep-analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            regulationId,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to run deep analysis: ${response.statusText}`);
        }

        const data: DeepAnalyzeResponse = await response.json();

        if (data.success && data.result) {
          setDeepAnalysisResult(data.result);
          return data.result;
        }

        return null;
      } catch (err) {
        console.error('[useGovernanceLens] Deep analysis error:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      } finally {
        setDeepAnalysisRunning(false);
      }
    },
    []
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setTier1Result(null);
    setDeepAnalysisResult(null);
    setError(null);
    setTier1Loading(false);
    setTier1Running(false);
    setDeepAnalysisLoading(false);
    setDeepAnalysisRunning(false);
  }, []);

  // Auto-fetch metadata on mount
  useEffect(() => {
    if (autoFetch) {
      fetchLenses();
      fetchRegulations();
    }
  }, [autoFetch, fetchLenses, fetchRegulations]);

  // Auto-fetch Tier 1 results if documentId is provided
  useEffect(() => {
    if (documentId && autoFetch) {
      fetchTier1Results(documentId);
    }
  }, [documentId, autoFetch, fetchTier1Results]);

  return {
    // Metadata
    availableLenses,
    availableRegulations,

    // Tier 1
    tier1Result,
    tier1Loading,
    tier1Running,

    // Tier 2
    deepAnalysisResult,
    deepAnalysisLoading,
    deepAnalysisRunning,

    // Error
    error,

    // Actions
    fetchLenses,
    fetchRegulations,
    fetchTier1Results,
    runTier1Analysis,
    fetchDeepAnalysis,
    runDeepAnalysis,
    clearError,
    reset,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function summarizeFindings(findings: Tier1Finding[]): {
  critical: number;
  major: number;
  minor: number;
  observation: number;
  total: number;
} {
  const summary = {
    critical: 0,
    major: 0,
    minor: 0,
    observation: 0,
    total: findings.length,
  };

  for (const finding of findings) {
    summary[finding.severity]++;
  }

  return summary;
}

export default useGovernanceLens;
